import { motion, AnimatePresence } from 'framer-motion'
import SEO from '../components/SEO'
import { Send, CheckCircle } from 'lucide-react'
import Footer from '../components/Footer'
import { useState } from 'react'

interface FieldErrors {
  name?: string
  email?: string
}

export default function Waitlist() {
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')
  const [errorMessage, setErrorMessage] = useState('')
  const [errors, setErrors] = useState<FieldErrors>({})
  const [touched, setTouched] = useState<Record<string, boolean>>({})

  function validate(form: HTMLFormElement): FieldErrors {
    const data = new FormData(form)
    const errs: FieldErrors = {}
    if (!data.get('name')?.toString().trim()) errs.name = 'Name is required'
    const email = data.get('email')?.toString().trim() || ''
    if (!email) errs.email = 'Email is required'
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) errs.email = 'Enter a valid email'
    return errs
  }

  function handleBlur(e: React.FocusEvent<HTMLInputElement>) {
    const field = e.target.name
    setTouched(prev => ({ ...prev, [field]: true }))
    const form = e.target.closest('form')
    if (form) {
      const errs = validate(form)
      setErrors(prev => ({ ...prev, [field]: errs[field as keyof FieldErrors] }))
    }
  }

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const field = e.target.name as keyof FieldErrors
    if (touched[field] && errors[field]) {
      const form = e.target.closest('form')
      if (form) {
        const errs = validate(form)
        setErrors(prev => ({ ...prev, [field]: errs[field] }))
      }
    }
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const form = e.currentTarget
    const errs = validate(form)
    setErrors(errs)
    setTouched({ name: true, email: true })

    const hasErrors = Object.keys(errs).length > 0
    if (hasErrors) {
      const firstErrorField = Object.keys(errs)[0]
      const el = form.querySelector<HTMLElement>(`[name="${firstErrorField}"]`)
      el?.focus()
      return
    }

    const data = new FormData(form)
    const name = data.get('name')?.toString().trim() || ''
    const email = data.get('email')?.toString().trim() || ''

    setStatus('loading')
    setErrorMessage('')

    try {
      const response = await fetch('/api/waitlist', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name,
          email,
        }),
      })

      const result = await response.json().catch(() => ({}))

      if (!response.ok) {
        throw new Error(result.error || 'Failed to join the waitlist. Please try again.')
      }

      setStatus('success')
    } catch (error: any) {
      console.error('Waitlist submission error:', error)
      setStatus('error')
      setErrorMessage(error.message || 'An unexpected error occurred.')
    }
  }

  function fieldClass(field: keyof FieldErrors) {
    const base = "w-full rounded-lg border bg-theme-surface px-4 py-3 text-sm text-theme placeholder:text-theme-muted outline-none transition-all duration-200 focus:shadow-[0_0_0_3px_rgba(255,90,31,0.1)] focus-visible:outline-none"
    const hasError = touched[field] && errors[field]
    return `${base} ${hasError ? 'border-red-500/60 focus:border-red-500/60' : 'border-theme focus:border-primary/60'}`
  }

  return (
    <>
      <SEO 
        title="Join Waitlist — Solarch"
        description="Join the waitlist for Solarch."
        canonical="/waitlist"
        noindex={true}
      />

      <div className="min-h-screen bg-theme-body pt-14">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="relative mx-auto max-w-3xl px-4 pt-20 pb-12 sm:px-6 lg:px-8 lg:pt-28 lg:pb-16 text-center">
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, ease: [0.32, 0.72, 0, 1] }}
            >
              <h1 className="font-heading text-4xl font-bold tracking-tight text-theme sm:text-5xl">
                Join the <span className="text-primary">Waitlist</span>
              </h1>
              <p className="mx-auto mt-4 max-w-lg text-lg text-theme-secondary leading-relaxed">
                Secure your spot. Be the first to know when we launch and get exclusive early access.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Form */}
        <section className="mx-auto max-w-xl px-4 pb-24 sm:px-6 lg:px-8">
          {status === 'success' ? (
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.4, ease: [0.32, 0.72, 0, 1] }}
              className="rounded-2xl border border-theme bg-theme-surface p-12 text-center"
            >
              <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-full bg-green-500/10 border border-green-500/20">
                <CheckCircle className="h-7 w-7 text-green-500" />
              </div>
              <h2 className="font-heading text-2xl font-bold text-theme mb-2">You're on the list!</h2>
              <p className="text-theme-secondary text-base max-w-sm mx-auto">
                Thank you for joining the waitlist. We will notify you as soon as early access opens.
              </p>
            </motion.div>
          ) : (
            <motion.form
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.32, 0.72, 0, 1] }}
              onSubmit={handleSubmit}
              noValidate
              className="rounded-2xl border border-theme bg-theme-elevated p-8 sm:p-10 space-y-6"
            >
              {status === 'error' && (
                <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg text-red-500 text-sm">
                  {errorMessage}
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label htmlFor="waitlist-name" className="block text-sm font-medium text-theme mb-2">
                    Your Name <span className="text-primary">*</span>
                  </label>
                  <input
                    type="text"
                    id="waitlist-name"
                    name="name"
                    placeholder="Jane Doe"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    className={fieldClass('name')}
                    disabled={status === 'loading'}
                  />
                  <AnimatePresence>
                    {touched.name && errors.name && (
                      <motion.p
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15 }}
                        className="mt-1.5 text-xs text-red-400"
                      >
                        {errors.name}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
                
                <div>
                  <label htmlFor="waitlist-email" className="block text-sm font-medium text-theme mb-2">
                    Email Address <span className="text-primary">*</span>
                  </label>
                  <input
                    type="email"
                    id="waitlist-email"
                    name="email"
                    placeholder="jane@example.com"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    className={fieldClass('email')}
                    disabled={status === 'loading'}
                  />
                  <AnimatePresence>
                    {touched.email && errors.email && (
                      <motion.p
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -4 }}
                        transition={{ duration: 0.15 }}
                        className="mt-1.5 text-xs text-red-400"
                      >
                        {errors.email}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </div>
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={status === 'loading'}
                  className="group w-full inline-flex items-center justify-center gap-2.5 rounded-lg bg-primary px-6 py-3.5 text-sm font-semibold text-white transition-all duration-200 hover:brightness-110 active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {status === 'loading' ? (
                    'Joining...'
                  ) : (
                    <>
                      <Send className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      Join Waitlist
                    </>
                  )}
                </button>
              </div>
            </motion.form>
          )}
        </section>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <Footer />
        </div>
      </div>
    </>
  )
}
